import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
// Added Mail icon for the email field
import { Mail, Lock, Eye, EyeOff, User, Phone } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useAuth } from '@/lib/AuthContext';
import axios from 'axios';

export default function AuthPage() {
  const { setUser, getUser } = useAuth();
  const urlParams = new URLSearchParams(window.location.search);
  const initialMode = urlParams.get('mode') || 'login';
  const user = getUser();
  const navigate = useNavigate();

  const [mode, setMode] = useState(initialMode); // login | signup | forgot
  
  // States separated for clarity and proper data handling
  const [name, setName] = useState('');
  const [email, setEmail] = useState(''); // Email state
  const [phone, setPhone] = useState(''); // Phone state (was incorrectly using email before)
  const [password, setPassword] = useState('');
  
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(true);
  const [otp, setOtp] = useState('');
  
  // NEW: State for Terms and Conditions
  const [acceptTerms, setAcceptTerms] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    const role = email.includes('admin') ? 'admin' : email.includes('instructor') ? 'instructor' : 'student';
    setUser({ email, name: email.split('@')[0], role, avatar: '' });
    window.location.href = role === 'admin' ? '/admin' : role === 'instructor' ? '/instructor' : '/dashboard';
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    // Check to ensure terms are accepted before calling API
    if (!acceptTerms) {
      alert("Please accept the terms and conditions to proceed.");
      return;
    }
    
    const formData = new FormData();
    formData.append("full_name", name);
    formData.append("email", email);           // Added Email
    formData.append("phone_number", phone);    // Fixed Phone Number mapping
    formData.append("password", password);

    try {
      const res = await axios.post("https://xceededge.com/signup.php", formData, { headers: { "Content-Type": "multipart/form-data" } });
      
      if(res && res.data.otp){
        setMode("signOTP");
        // Store context for OTP
        setUser({ phone: res.data.phone_number, email: email, name: name, role: 'student', avatar: '' });
        navigate('/sign-otp' + `?phone=${res.data.phone_number}&id=${res.data.id}`);
      } else {
        console.log("error", res.data);
        alert(res.data.message || "Signup failed. Please try again.");
      }
    } catch (error) {
      console.error("Signup request failed:", error);
      alert("Server error occurred. Please try again.");
    }
  };

  const handleGoogleAuth = () => {
    setUser({ email: 'student@gmail.com', name: 'Student User', role: 'student', avatar: '' });
    window.location.href = '/dashboard';
  };

  const handleForgotSubmit = (e) => {
    e.preventDefault();
    if (!otpSent) { setOtpSent(true); return; }
    setMode('login');
    setOtpSent(false);
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("phone_number", user?.phone);
    formData.append("otp", otp);
    const res = await axios.post("/verify_otp.php", formData, { headers: { "Content-Type": "multipart/form-data" } });
    
    if(res){
      if(res.data.otp){
        setMode("");
        window.location.href = '/dashboard';
      } else {
        console.log("error", res.data);
      }
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center mb-2 mt-5">
  <img 
    src="https://www.xceededge.in/XceedTransLogo.png" 
    alt="XceedEdge Logo" 
    className="h-20 w-auto object-contain"
  />
</Link>
          <h1 className="font-heading text-2xl font-bold">
            {mode === 'login' && 'Welcome Back'}
            {mode === 'signup' && 'Create Account'}
            {mode === 'forgot' && 'Reset Password'}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {mode === 'login' && 'Sign in to continue learning'}
            {mode === 'signup' && 'Start your learning journey today'}
            {mode === 'forgot' && 'Enter your email to receive a reset code'}
          </p>
        </div>

        <div className="bg-card rounded-2xl border shadow-lg p-6 sm:p-8">
          <>
            <form onSubmit={handleSignup} className="space-y-4">
              
              {/* Full Name */}
              <div>
                <Label>Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input value={name} onChange={e => setName(e.target.value)} className="pl-10" placeholder="Enter Your Name" required />
                </div>
              </div>

              {/* Email (NEW) */}
              <div>
                <Label>Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input 
                    type="email" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    className="pl-10" 
                    placeholder="Enter Your Email" 
                    required 
                  />
                </div>
              </div>

              {/* Phone Number */}
              <div>
                <Label>Phone Number</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  {/* Fixed value mapping to `phone` instead of `email` */}
                  <Input 
                    type="tel" 
                    value={phone} 
                    onChange={e => setPhone(e.target.value)} 
                    className="pl-10" 
                    placeholder="Enter Phone Number" 
                    required 
                    maxLength={10}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <Label>Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    placeholder="••••••••"
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {/* Terms and Conditions Checkbox (NEW) */}
              <div className="flex items-center space-x-2 pt-2 pb-1">
                <input 
                  type="checkbox" 
                  id="terms" 
                  required
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                />
                <Label htmlFor="terms" className="text-sm font-normal text-muted-foreground cursor-pointer">
                  I accept the <Link to="/terms" className="text-primary hover:underline">terms and conditions</Link>
                </Label>
              </div>

              {/* Disabled button until terms are checked */}
              <Button type="submit" className="w-full h-11" disabled={!acceptTerms}>
                Create Account
              </Button>
            </form>

            {/* <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t" /></div>
              <div className="relative flex justify-center"><span className="bg-card px-3 text-xs text-muted-foreground">or continue with</span></div>
            </div>

            <Button variant="outline" className="w-full gap-2" onClick={handleGoogleAuth}>
              <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Google
            </Button>  */}

            <p className="text-center text-sm text-muted-foreground mt-6">
              Already have an account? <button onClick={() => navigate('/login')} className="text-primary font-medium hover:underline">Sign in</button>
            </p>
          </>
        </div>
      </div>
    </motion.div>
  );
}